// CadProjectCardPage — публичная карточка CAD-проекта.
// Маршрут: /main/cad-projects/:id
//
// Показывает:
//   - превью проекта (PNG из thumbnail)
//   - название, описание, автор (аватар + ник)
//   - метрики: ❤ лайки, 👁 просмотры, 💬 комменты, 🍴 если форк
//   - кнопки: ❤ Лайк, 🍴 Скопировать себе, 🚩 Жалоба
//   - комментарии (список + форма добавления)
//   - кнопка «Открыть в редакторе» (только для моих проектов)
//
// При первом открытии засчитывается просмотр (1 раз на юзера через
// kubikon_cad_views unique-индекс).

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as CadAPI from '../../API/KubikonCadService';
import UserService from '../../API/UserService';
import { FOLDER_PROFILE } from '../../API/API';
import { getCurrentUserId } from './cloudIO';
import cl from './CadProjectCardPage.module.css';


function formatDate(s) {
    if (!s) return '';
    try {
        const d = new Date(s);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString('ru', {
            day: 'numeric', month: 'long', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch (_) { return ''; }
}


// === Заглушка превью если нет thumbnail ===
const PreviewBox = ({ thumbnail }) => {
    if (thumbnail && thumbnail.startsWith('data:image')) {
        return <img src={thumbnail} alt="превью" className={cl.previewImg}/>;
    }
    return (
        <div className={cl.previewEmpty}>
            <div className={cl.previewIcon}>🧊</div>
            <div className={cl.previewLabel}>Нет превью</div>
        </div>
    );
};


// === Бейдж автора ===
const AuthorBadge = ({ profile }) => {
    if (!profile) return null;
    const ava = profile.photo || profile.avatar
        ? `${FOLDER_PROFILE}${profile.photo || profile.avatar}`
        : null;
    const name = profile.first_name || profile.firstName || profile.login || 'Аноним';
    return (
        <div className={cl.author}>
            {ava ? (
                <img src={ava} alt={name} className={cl.authorAva}/>
            ) : (
                <div className={cl.authorAvaPlaceholder}>{name[0]?.toUpperCase()}</div>
            )}
            <div className={cl.authorInfo}>
                <div className={cl.authorName}>{name}</div>
                <div className={cl.authorRole}>Автор</div>
            </div>
        </div>
    );
};


// === Один комментарий ===
const CommentItem = ({ comment, myUserId, onDelete }) => {
    const [profile, setProfile] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const p = await UserService.getProfileData(comment.user_id);
                if (!cancelled) setProfile(p.data || p);
            } catch (_) { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [comment.user_id]);

    const ava = profile && (profile.photo || profile.avatar)
        ? `${FOLDER_PROFILE}${profile.photo || profile.avatar}`
        : null;
    const name = profile?.first_name || profile?.login || 'Пользователь';
    const isMine = myUserId && comment.user_id === myUserId;

    return (
        <div className={cl.comment}>
            {ava ? (
                <img src={ava} alt={name} className={cl.commentAva}/>
            ) : (
                <div className={cl.commentAvaPlaceholder}>{name[0]?.toUpperCase()}</div>
            )}
            <div className={cl.commentBody}>
                <div className={cl.commentHead}>
                    <span className={cl.commentName}>{name}</span>
                    <span className={cl.commentDate}>{formatDate(comment.created_at)}</span>
                </div>
                <div className={cl.commentText}>{comment.text}</div>
            </div>
            {isMine && (
                <button
                    className={cl.commentDelBtn}
                    onClick={() => onDelete(comment.id)}
                    title="Удалить"
                >
                    🗑
                </button>
            )}
        </div>
    );
};


// === Главный компонент ===
const CadProjectCardPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const projectId = Number(id);
    const myUserId = getCurrentUserId();

    const [project, setProject] = useState(null);
    const [authorProfile, setAuthorProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [liked, setLiked] = useState(false);
    const [likesCount, setLikesCount] = useState(0);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [sending, setSending] = useState(false);
    const [similar, setSimilar] = useState([]);

    // Загрузить проект
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await CadAPI.getProjectPreview(projectId, myUserId);
                if (cancelled) return;
                setProject(res.data);
                setLikesCount(res.data.likes_count || 0);
                // Засчитываем просмотр
                if (myUserId) {
                    CadAPI.registerView(projectId, myUserId).catch(() => {});
                }
            } catch (e) {
                if (cancelled) return;
                const code = e?.response?.status;
                if (code === 403) setError('Этот проект приватный — автор его не публиковал.');
                else if (code === 404) setError('Проект не найден.');
                else setError('Не удалось загрузить проект: ' + (e?.message || e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [projectId, myUserId]);

    // Загрузить профиль автора
    useEffect(() => {
        if (!project?.user_id) return;
        let cancelled = false;
        (async () => {
            try {
                const p = await UserService.getProfileData(project.user_id);
                if (!cancelled) setAuthorProfile(p.data || p);
            } catch (_) { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [project?.user_id]);

    // Загрузить статус лайка
    useEffect(() => {
        if (!myUserId || !projectId) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await CadAPI.getLikeStatus(projectId, myUserId);
                if (!cancelled) setLiked(!!r.data?.liked);
            } catch (_) { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [projectId, myUserId]);

    // Загрузить комментарии
    const loadComments = useCallback(async () => {
        try {
            const r = await CadAPI.getComments(projectId);
            setComments(r.data?.items || []);
        } catch (_) { /* ignore */ }
    }, [projectId]);

    useEffect(() => { loadComments(); }, [loadComments]);

    // Загрузить похожие проекты (популярные из ленты, исключая текущий)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await CadAPI.getFeed({ sort: 'popular', limit: 8 });
                if (cancelled) return;
                const items = (r.data?.items || [])
                    .filter((p) => p.id !== projectId)
                    .slice(0, 4);
                setSimilar(items);
            } catch (_) { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [projectId]);

    const onToggleLike = async () => {
        if (!myUserId) { alert('Войди в аккаунт чтобы ставить лайки'); return; }
        try {
            const r = await CadAPI.toggleLike(projectId, myUserId);
            setLiked(!!r.data?.liked);
            setLikesCount(r.data?.likes_count || 0);
        } catch (e) {
            alert('Не удалось: ' + (e?.message || e));
        }
    };

    const onFork = async () => {
        if (!myUserId) { alert('Войди в аккаунт чтобы копировать проекты'); return; }
        if (!window.confirm('Скопировать этот проект себе?')) return;
        try {
            const r = await CadAPI.forkProject(projectId, myUserId);
            const newId = r.data?.id;
            if (newId) navigate(`/main/cad-editor/${newId}`);
        } catch (e) {
            alert('Не удалось скопировать: ' + (e?.message || e));
        }
    };

    const onReport = async () => {
        if (!myUserId) { alert('Войди в аккаунт'); return; }
        const reason = window.prompt('Опиши причину жалобы:');
        if (!reason || !reason.trim()) return;
        try {
            await CadAPI.reportProject(projectId, myUserId, reason.trim());
            alert('Жалоба отправлена. Спасибо.');
        } catch (e) {
            alert('Не удалось: ' + (e?.message || e));
        }
    };

    const onSendComment = async (e) => {
        e?.preventDefault();
        const txt = newComment.trim();
        if (!txt) return;
        if (!myUserId) { alert('Войди в аккаунт'); return; }
        setSending(true);
        try {
            await CadAPI.addComment(projectId, myUserId, txt);
            setNewComment('');
            await loadComments();
        } catch (e) {
            alert('Не удалось: ' + (e?.message || e));
        } finally {
            setSending(false);
        }
    };

    const onDeleteComment = async (commentId) => {
        if (!myUserId) return;
        if (!window.confirm('Удалить комментарий?')) return;
        try {
            await CadAPI.deleteComment(commentId, myUserId);
            await loadComments();
        } catch (e) {
            alert('Не удалось: ' + (e?.message || e));
        }
    };

    const isMine = project && myUserId === project.user_id;

    if (loading) return <div className={cl.page}><div className={cl.loader}>Загрузка...</div></div>;
    if (error) {
        return (
            <div className={cl.page}>
                <div className={cl.errorBox}>
                    <h2>Ошибка</h2>
                    <p>{error}</p>
                    <Link to="/main/cad-editor" className={cl.btn}>← Назад</Link>
                </div>
            </div>
        );
    }
    if (!project) return null;

    return (
        <div className={cl.page}>
            <div className={cl.scrollWrap}>
                <Link to="/main/cad-editor" className={cl.backLink}>← Назад к проектам</Link>

                <div className={cl.hero}>
                    <div className={cl.heroPreview}>
                        <PreviewBox thumbnail={project.thumbnail}/>
                    </div>
                    <div className={cl.heroInfo}>
                        <h1 className={cl.title}>{project.title || 'Без названия'}</h1>
                        {project.description && (
                            <p className={cl.description}>{project.description}</p>
                        )}
                        <AuthorBadge profile={authorProfile}/>
                        <div className={cl.metaRow}>
                            <span className={cl.metaItem}>📅 {formatDate(project.updated_at)}</span>
                            {project.forked_from_id && (
                                <Link
                                    to={`/main/cad-projects/${project.forked_from_id}`}
                                    className={cl.metaItem}
                                    title="Это копия чужого проекта"
                                >
                                    🍴 Форк
                                </Link>
                            )}
                        </div>

                        {Array.isArray(project.tags) && project.tags.length > 0 && (
                            <div className={cl.tags}>
                                {project.tags.map((t) => (
                                    <span key={t} className={cl.tag}>#{t}</span>
                                ))}
                            </div>
                        )}

                        <div className={cl.stats}>
                            <span>❤ {likesCount}</span>
                            <span>👁 {project.views_count || 0}</span>
                            <span>💬 {project.comments_count || 0}</span>
                        </div>

                        <div className={cl.actions}>
                            <button
                                className={`${cl.btn} ${liked ? cl.btnLiked : cl.btnPrimary}`}
                                onClick={onToggleLike}
                            >
                                {liked ? '❤ В избранном' : '❤ Лайк'}
                            </button>
                            {isMine ? (
                                <Link
                                    to={`/main/cad-editor/${project.id}`}
                                    className={`${cl.btn} ${cl.btnPrimary}`}
                                >
                                    ✏️ Открыть в редакторе
                                </Link>
                            ) : (
                                <button
                                    className={`${cl.btn} ${cl.btnPrimary}`}
                                    onClick={onFork}
                                >
                                    🍴 Скопировать себе
                                </button>
                            )}
                            {!isMine && (
                                <button className={`${cl.btn} ${cl.btnDanger}`} onClick={onReport}>
                                    🚩 Жалоба
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className={cl.section}>
                    <h2 className={cl.sectionTitle}>Комментарии</h2>
                    <form className={cl.commentForm} onSubmit={onSendComment}>
                        <textarea
                            className={cl.commentInput}
                            placeholder="Напиши комментарий..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            maxLength={2000}
                            rows={3}
                        />
                        <button
                            type="submit"
                            className={`${cl.btn} ${cl.btnPrimary}`}
                            disabled={sending || !newComment.trim()}
                        >
                            {sending ? 'Отправка...' : 'Отправить'}
                        </button>
                    </form>

                    <div className={cl.commentsList}>
                        {comments.length === 0 ? (
                            <div className={cl.emptyComments}>Пока нет комментариев</div>
                        ) : (
                            comments.map((c) => (
                                <CommentItem
                                    key={c.id}
                                    comment={c}
                                    myUserId={myUserId}
                                    onDelete={onDeleteComment}
                                />
                            ))
                        )}
                    </div>
                </div>

                {similar.length > 0 && (
                    <div className={cl.section}>
                        <h2 className={cl.sectionTitle}>Похожие проекты</h2>
                        <div className={cl.similarGrid}>
                            {similar.map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/main/cad-projects/${p.id}`}
                                    className={cl.similarCard}
                                >
                                    <div className={cl.similarPreview}>
                                        <PreviewBox thumbnail={p.thumbnail}/>
                                    </div>
                                    <div className={cl.similarBody}>
                                        <div className={cl.similarTitle}>
                                            {p.title || 'Без названия'}
                                        </div>
                                        <div className={cl.similarStats}>
                                            <span>❤ {p.likes_count || 0}</span>
                                            <span>👁 {p.views_count || 0}</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CadProjectCardPage;
